/**
 * One payer's open invoices, under its row on the Aged debt tab (docs/features/v1-completion/
 * L_credit_control_spec.md §4.6; mock-up L_credit_control.html states 2 and 3): oldest due first, each linking to
 * its invoice page, with Record dispute on an invoice that has none and Resolve dispute on one that has. The
 * buttons show only where the invoice's own response carries `record_dispute_url` / `resolve_dispute_url`
 * (credit_control:manage); the write posts to that URL and the page re-reads.
 *
 * Self-loading from the payer's `invoices_url`; `reloadKey` re-reads it when the page's lists have changed.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AgedDebtInvoice,
  AgedDebtInvoiceList,
  AgedDebtPayer,
} from "@shared-core/data/aged-debt-types";
import { daysLateLabel } from "@shared-core/domain/credit-control-presentation";
import {
  formatQuotedPrice,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import { paths } from "@shared-core/routing/routes";
import { ListPagination } from "../../patterns/ListPagination";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import { Skeleton } from "../../primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { RecordDisputeDialog, ResolveDisputeDialog } from "./DisputeDialogs";

const PAGE_SIZE = 25;

type Load =
  | { kind: "loading"; previous: AgedDebtInvoiceList | null }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; list: AgedDebtInvoiceList };

type Dialog =
  | { kind: "record"; invoice: AgedDebtInvoice }
  | { kind: "resolve"; invoice: AgedDebtInvoice };

export function AgedDebtInvoicesPanel({
  payer,
  reloadKey,
  onClose,
  onDisputesChanged,
}: {
  payer: AgedDebtPayer;
  /** Changes whenever the page's lists were re-read: this re-reads too. */
  reloadKey: number;
  onClose: () => void;
  /** A dispute was recorded or resolved: the page re-reads the payers' disputed amounts. */
  onDisputesChanged: () => void;
}) {
  const adapter = useDataAdapter();
  const [page, setPage] = useState(1);
  const [load, setLoad] = useState<Load>({ kind: "loading", previous: null });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Bumped when this panel wrote a dispute: it re-reads its own invoices as well as telling the page.
  const [writes, setWrites] = useState(0);

  const invoicesUrl = payer.invoices_url;
  useEffect(() => {
    let cancelled = false;
    setLoad((s) => ({
      kind: "loading",
      previous:
        s.kind === "loaded" ? s.list : s.kind === "loading" ? s.previous : null,
    }));
    adapter
      .listAgedDebtInvoices(
        { invoices_url: invoicesUrl },
        { page, pageSize: PAGE_SIZE },
      )
      .then((list) => {
        if (!cancelled) setLoad({ kind: "loaded", list });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setLoad({
            kind: "failed",
            message:
              err instanceof Error ? err.message : "The invoices did not load.",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, invoicesUrl, page, reloadKey, writes]);

  const list =
    load.kind === "loaded"
      ? load.list
      : load.kind === "loading"
        ? load.previous
        : null;

  return (
    <section
      aria-label={`${payer.payer_name}: open invoices`}
      className="space-y-3 rounded-lg border border-border bg-card p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-medium">
          {payer.payer_name} · open invoices
        </h2>
        <span className="flex gap-2">
          <Button asChild size="sm" variant="outline">
            <Link
              to={paths.customerSettings({ customerId: payer.customer_code })}
            >
              Open Settings
            </Link>
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={onClose}>
            Back to all payers
          </Button>
        </span>
      </div>

      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}
      {load.kind === "failed" && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            Could not load the open invoices: {load.message}
          </AlertDescription>
        </Alert>
      )}
      {!list && load.kind === "loading" && (
        <div className="space-y-2" aria-label="Loading the open invoices">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {list && (
        <div aria-busy={load.kind === "loading"}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="text-right">Days late</TableHead>
                <TableHead className="text-right">Owed</TableHead>
                <TableHead>Xero</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.items.map((invoice) => (
                <TableRow key={invoice.invoice_no}>
                  <TableCell className="font-medium">
                    <Link
                      to={paths.invoiceDetail({
                        invoiceNo: invoice.invoice_no,
                      })}
                      className="hover:underline"
                    >
                      {invoice.invoice_no}
                    </Link>{" "}
                    {invoice.dispute && (
                      <Badge variant="warning" className="font-normal">
                        Disputed
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {invoice.order_no ? (
                      <Link
                        to={paths.orderDetail({ orderNo: invoice.order_no })}
                        className="hover:underline"
                      >
                        {invoice.order_no}
                      </Link>
                    ) : (
                      "–"
                    )}
                  </TableCell>
                  <TableCell>{invoice.customer_name ?? "–"}</TableCell>
                  <TableCell className="tabular-nums">
                    {formatShortDateOrDash(invoice.due_date)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {daysLateLabel(invoice.days_overdue)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatQuotedPrice(invoice.outstanding_pence)}
                  </TableCell>
                  <TableCell>{invoice.state_label}</TableCell>
                  <TableCell>
                    {invoice.dispute?.resolve_dispute_url ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-label={`Resolve dispute on ${invoice.invoice_no}`}
                        onClick={() => setDialog({ kind: "resolve", invoice })}
                      >
                        Resolve dispute…
                      </Button>
                    ) : invoice.record_dispute_url ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        aria-label={`Record dispute on ${invoice.invoice_no}`}
                        onClick={() => setDialog({ kind: "record", invoice })}
                      >
                        Record dispute…
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-semibold hover:bg-transparent">
                <TableCell colSpan={5}>
                  {payer.open_invoice_count} open{" "}
                  {payer.open_invoice_count === 1 ? "invoice" : "invoices"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatQuotedPrice(payer.outstanding_pence)}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
          <ListPagination
            page={list.page}
            totalPages={list.totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      {dialog?.kind === "record" && (
        <RecordDisputeDialog
          invoice={{
            invoiceNo: dialog.invoice.invoice_no,
            orderNo: dialog.invoice.order_no,
            dueOn: dialog.invoice.due_date,
            payerName: payer.payer_name,
            amountPence: dialog.invoice.outstanding_pence,
            amountLabel: "Owed",
          }}
          onClose={() => setDialog(null)}
          onSubmit={async (reason) => {
            await adapter.recordDispute(dialog.invoice, { reason });
            setNotice(`Dispute recorded on ${dialog.invoice.invoice_no}.`);
            setWrites((n) => n + 1);
            onDisputesChanged();
          }}
        />
      )}
      {dialog?.kind === "resolve" && dialog.invoice.dispute && (
        <ResolveDisputeDialog
          dispute={{
            invoiceNo: dialog.invoice.invoice_no,
            amountPence: dialog.invoice.gross_pence,
            raisedAt: dialog.invoice.dispute.raised_at,
            raisedByName: dialog.invoice.dispute.raised_by_name,
            reason: dialog.invoice.dispute.reason,
          }}
          onClose={() => setDialog(null)}
          onSubmit={async (note) => {
            await adapter.resolveDispute(dialog.invoice.dispute!, { note });
            setNotice(`Dispute resolved on ${dialog.invoice.invoice_no}.`);
            setWrites((n) => n + 1);
            onDisputesChanged();
          }}
        />
      )}
    </section>
  );
}
